import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck, PencilLine, Star } from "lucide-react";
import { toast } from "sonner";
import { ProfileFilters } from "./ProfileFilters";
import { StatusTabs } from "./StatusTabs";
import { SignedThumb } from "./MediaCard";
import { DataTable, Pagination } from "./DataTable";
import { ExportButtons } from "./ExportButtons";
import { ConfirmDialog } from "./ConfirmDialog";
import { StatusBadge } from "../register/UnderReview";
import { emptyFilters, fetchProfiles, fetchAllProfiles, fmtDate, PROFILE_EXPORT_COLUMNS } from "../../lib/adminApi";

const PAGE_SIZE = 20;

export const PROFILE_COLUMNS = [
  { key: "thumb", label: "", render: (r) => <SignedThumb path={r.primary_photo} name={r.full_name} /> },
  { key: "registration_no", label: "Reg. No", sortable: true },
  {
    key: "full_name", label: "Name", sortable: true,
    render: (r) => (
      <span className="inline-flex items-center gap-1.5 font-semibold text-[#7A0016]">
        {r.full_name}
        {r.is_verified && <BadgeCheck className="h-4 w-4 text-emerald-600" aria-label="Verified" />}
        {r.is_featured && <Star className="h-3.5 w-3.5 fill-[#D4AF37] text-[#D4AF37]" aria-label="Featured" />}
      </span>
    ),
  },
  { key: "age", label: "Age", sortable: true },
  { key: "height", label: "Height" },
  { key: "religion", label: "Religion / Caste", sortable: true, render: (r) => [r.religion, r.caste].filter(Boolean).join(" / ") || "-" },
  { key: "highest_qualification", label: "Education" },
  { key: "occupation", label: "Occupation" },
  { key: "city", label: "City" },
  {
    key: "status", label: "Status", sortable: true,
    render: (r) => (
      <span className="inline-flex items-center gap-1.5">
        <StatusBadge status={r.status} />
        {r.has_pending_edit && <span title="Edited by client" className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase text-[#C59B27]"><PencilLine className="h-3 w-3" />Edited</span>}
      </span>
    ),
  },
  { key: "created_at", label: "Registered", sortable: true, render: (r) => fmtDate(r.created_at) },
];

// bulkActions: [{ id, label, confirm: (n) => ({title, description, needsReason}), run: (ids, reason) }]
export function ProfileListPanel({ base, bulkActions = [], rowActions, columns = PROFILE_COLUMNS, exportName, exportTitle, hideStatus, selectable = true, reloadKey, statusTabs = false }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [debounced, setDebounced] = useState(emptyFilters);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [action, setAction] = useState(null);
  const baseKey = JSON.stringify(base || {});
  const effBaseKey = JSON.stringify({ ...JSON.parse(baseKey), ...(statusTabs && tab ? { status: tab } : {}) });

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(filters); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchProfiles({ filters: debounced, base: JSON.parse(effBaseKey), sort, page, pageSize: PAGE_SIZE }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [debounced, effBaseKey, sort, page]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const startBulk = (a) => {
    const ids = [...selected];
    setAction({
      ...a.confirm(ids.length),
      confirmLabel: a.label,
      run: async (reason) => {
        try {
          await a.run(ids, reason);
          toast.success(`${a.label}: ${ids.length} profile(s) updated`);
          setSelected(new Set());
          load();
        } catch (e) {
          toast.error(e.message);
        }
      },
    });
  };

  return (
    <div className="space-y-4" data-testid="profile-list-panel">
      {statusTabs && <StatusTabs filters={debounced} base={JSON.parse(baseKey)} value={tab} onChange={(v) => { setTab(v); setPage(1); }} />}
      <ProfileFilters value={filters} onChange={setFilters} hideStatus={hideStatus || statusTabs} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2" data-testid="bulk-actions">
          {selectable && selected.size > 0 && (
            <>
              <span className="text-xs font-semibold text-[#7A0016]">{selected.size} selected</span>
              {bulkActions.map((a) => (
                <button key={a.id} type="button" onClick={() => startBulk(a)} data-testid={`bulk-${a.id}-button`} className="rounded-full bg-[#7A0016] px-3 py-1.5 text-xs font-semibold text-[#F5E6BA] hover:bg-[#4A000E]">
                  {a.label}
                </button>
              ))}
            </>
          )}
        </div>
        <ExportButtons fetchRows={() => fetchAllProfiles(debounced, JSON.parse(effBaseKey), sort)} columns={PROFILE_EXPORT_COLUMNS} name={exportName} title={exportTitle} />
      </div>
      <DataTable
        columns={columns}
        rows={data.rows}
        loading={loading}
        selectable={selectable && bulkActions.length > 0}
        selected={selected}
        onSelect={setSelected}
        sort={sort}
        onSort={setSort}
        rowActions={rowActions ? (r) => rowActions(r, load) : null}
        onRow={(r) => navigate(`/admin/profiles/${r.id}`)}
      />
      <Pagination page={page} pageSize={PAGE_SIZE} count={data.count} onPage={setPage} />
      <ConfirmDialog action={action} onClose={() => setAction(null)} />
    </div>
  );
}
